import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const lineKey = (p) => `${p.id}-${p.flavor}-${p.weight}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const saved = localStorage.getItem("gt_cart");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem("gt_cart", JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, opts = {}) => {
    const flavor = opts.flavor || product.flavor;
    const weight = opts.weight || product.weight;
    const qty = opts.qty || 1;
    const message = opts.message || "";
    setItems((prev) => {
      const key = `${product.id}-${flavor}-${weight}`;
      const existing = prev.find((i) => lineKey(i) === key);
      if (existing) {
        return prev.map((i) => (lineKey(i) === key ? { ...i, qty: i.qty + qty } : i));
      }
      return [
        ...prev,
        { id: product.id, name: product.name, slug: product.slug, image: product.image, price: product.price, flavor, weight, qty, message },
      ];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => lineKey(i) !== key)), []);

  const updateQty = useCallback((key, qty) =>
    setItems((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, qty: Math.max(1, qty) } : i))), []);

  const updateItem = useCallback((key, patch) =>
    setItems((prev) => prev.map((i) => (lineKey(i) === key ? { ...i, ...patch } : i))), []);

  const clearCart = useCallback(() => setItems([]), []);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);

  const value = useMemo(
    () => ({ items, addItem, removeItem, updateQty, updateItem, clearCart, count, subtotal, isOpen, setIsOpen, lineKey }),
    [items, addItem, removeItem, updateQty, updateItem, clearCart, count, subtotal, isOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
